import { Link, createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import { CefrBandBreakdown } from "../components/CefrBandBreakdown";
import { CefrProgressCard } from "../components/CefrProgressCard";
import { api } from "../lib/api";
import {
  useActiveLanguageIsUnseeded,
  useLanguageCode,
} from "../lib/activeLanguage";
import { requireAuth } from "../lib/requireAuth";
import { UnseededLanguageLanding } from "../lib/UnseededLanguageLanding";
import { PageEyebrow } from "../components/PageEyebrow";
import { Centered } from "../components/Centered";

export const Route = createFileRoute("/progress")({
  beforeLoad: ({ context, location }) =>
    requireAuth(context.queryClient, location.href),
  component: ProgressIndex,
});

/**
 * The "what level am I?" surface. /skills is the alphabetical reference
 * of every skill; /progress answers the higher-level question of how
 * you're doing across the CEFR bands. Renders:
 *
 *   1. The aggregate CEFR card (same as /home).
 *   2. Per-CEFR-band bars (A1, A2, B1, …) so the level breakdown is
 *      visible at a glance — Jorge's specific ask.
 *   3. A tail nudge pointing into /skills for the per-skill timeline.
 *
 * No per-skill list here — that's /skills, and duplicating it would
 * just make the two pages indistinguishable.
 */
function ProgressIndex() {
  const language = useLanguageCode();
  const unseeded = useActiveLanguageIsUnseeded();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["skills-mastery", language],
    queryFn: async () => {
      const { data, error } = await api.GET("/skills/mastery", {
        params: { query: { language } },
      });
      if (error) throw error;
      return data;
    },
    staleTime: 30_000,
  });

  if (unseeded) return <UnseededLanguageLanding />;
  if (isLoading) return <Centered>Loading your progress…</Centered>;
  if (isError || !data) return <Centered>Could not load progress.</Centered>;

  const nTouched = data.filter((s) => s.nAttempts > 0).length;

  return (
    <section className="mx-auto max-w-3xl px-4 sm:px-6 py-8 sm:py-12">
      <PageEyebrow label="Your progress" mono={`${language} · CEFR bands`} />
      <h1 className="font-serif text-3xl text-ink-900 mb-2 tracking-tight">
        Where you are
      </h1>
      <p className="text-ink-500 text-sm mb-5 sm:mb-6 max-w-2xl">
        The aggregate first, then the breakdown by CEFR level. Each band's bar
        is the average <strong>memory</strong> across the skills you've actually
        practiced in that band — empty bars mean you haven't started yet, not
        that you failed.
      </p>

      <CefrProgressCard language={language} />

      <CefrBandBreakdown skills={data} />

      {nTouched === 0 ? (
        <div className="text-center py-8 border-t border-ink-100 mt-4">
          <p className="text-ink-500 mb-6">
            Once you submit a few attempts, every skill page gets a timeline.
          </p>
          <Link
            to="/practice"
            className="inline-block bg-accent px-5 py-3 text-accent-fg hover:bg-accent-hover transition-colors"
          >
            Start practice →
          </Link>
        </div>
      ) : (
        <p className="text-sm text-ink-500 mt-2">
          For the per-skill timeline (memory + stability + ability belief), open
          the skill from{" "}
          <Link
            to="/skills"
            className="underline underline-offset-2 decoration-1 hover:text-ink-900"
          >
            Skills
          </Link>{" "}
          — each row links into{" "}
          <code className="font-mono text-xs">
            /progress/skill/&lt;code&gt;
          </code>
          .
        </p>
      )}
    </section>
  );
}
